import { getStoredLanguage } from "../context/I18nContext";

function normalizeApiBase(rawValue) {
  if (typeof rawValue !== "string") return null;

  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  const withoutTrailingSlash = trimmed.replace(/\/$/, "");

  if (
    withoutTrailingSlash.startsWith("http://") ||
    withoutTrailingSlash.startsWith("https://")
  ) {
    return withoutTrailingSlash;
  }

  if (withoutTrailingSlash.startsWith("//")) {
    return `https:${withoutTrailingSlash}`;
  }

  if (
    withoutTrailingSlash.startsWith("localhost") ||
    withoutTrailingSlash.startsWith("127.0.0.1")
  ) {
    return `http://${withoutTrailingSlash}`;
  }

  return `https://${withoutTrailingSlash}`;
}

const configuredApiBase = normalizeApiBase(process.env.REACT_APP_BACKEND_URL);

const defaultApiBase = "https://api.casaclassroom.com";
// Local backend: set REACT_APP_BACKEND_URL=http://localhost:8001
const API_BASE = configuredApiBase || defaultApiBase;
const AUTH_BASE = `${API_BASE}/api/auth`;

async function request(path, { accessToken, method = "GET", body } = {}) {
  const headers = {
    "Content-Type": "application/json",
    "Accept-Language": getStoredLanguage(),
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  let response;
  try {
    response = await fetch(`${AUTH_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    const error = new Error(
      "Network request failed. Check REACT_APP_BACKEND_URL and API availability.",
    );
    error.cause = err;
    throw error;
  }

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || "Une erreur est survenue";
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function login(email, password) {
  return request("/login/", {
    method: "POST",
    body: { email, password },
  });
}

export function loginWithGoogle(idToken) {
  return request("/google-login/", {
    method: "POST",
    body: { token: idToken },
  });
}

export function register({ name, email, password, role, resume, phoneNumber }) {
  if (resume) {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("phone_number", phoneNumber || "");
    if (role) {
      formData.append("role", role);
    }
    formData.append("resume", resume);

    return (async () => {
      const response = await fetch(`${AUTH_BASE}/register/`, {
        method: "POST",
        headers: {
          "Accept-Language": getStoredLanguage(),
        },
        body: formData,
      });

      const isJson = response.headers
        .get("content-type")
        ?.includes("application/json");
      const payload = isJson ? await response.json() : {};

      if (!response.ok) {
        const message = payload.error || "Une erreur est survenue";
        const error = new Error(message);
        error.status = response.status;
        throw error;
      }

      return payload;
    })();
  }

  return request("/register/", {
    method: "POST",
    body: {
      name,
      email,
      password,
      phone_number: phoneNumber || "",
      ...(role ? { role } : {}),
    },
  });
}

export function requestPasswordReset(email) {
  return request("/password/reset/", {
    method: "POST",
    body: { email, mode: "otp" },
  });
}

export function verifyPasswordResetOtp({ email, otp }) {
  return request("/password/reset/verify/", {
    method: "POST",
    body: { email, otp },
  });
}

export function setPasswordWithResetToken({ email, token, password }) {
  return request("/password/reset/set-password/", {
    method: "POST",
    body: { email, token, password },
  });
}

export function confirmPasswordReset({ email, otp, password }) {
  return request("/password/reset/confirm/", {
    method: "POST",
    body: { email, otp, password },
  });
}

export function logout({ accessToken, refreshToken }) {
  return request("/logout/", {
    method: "POST",
    accessToken,
    body: refreshToken ? { refresh: refreshToken } : undefined,
  });
}

export function me(accessToken) {
  return request("/me/", {
    accessToken,
  });
}

export function updateProfile(accessToken, payload) {
  return request("/profile/", {
    method: "PATCH",
    accessToken,
    body: payload,
  });
}

export async function uploadAvatar(accessToken, file) {
  if (!accessToken) {
    throw new Error("Unauthorized");
  }
  const formData = new FormData();
  formData.append("avatar", file);

  const response = await fetch(`${AUTH_BASE}/profile/avatar/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Accept-Language": getStoredLanguage(),
    },
    body: formData,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || "Une erreur est survenue";
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function refresh(refreshToken) {
  return request("/token/refresh/", {
    method: "POST",
    body: { refresh: refreshToken },
  });
}

export const apiBaseUrl = API_BASE;
